import { z } from 'zod'

import { Chain } from '@/generated/proto/bridge/models/chain'
import { Referral } from '@/generated/proto/bridge/models/referrals'
import { Token } from '@/generated/proto/bridge/models/token'

import { bridgelessRestApiClient } from '../clients'

/**
 * Backend returns snake_case fields (chain_id, token_id, is_wrapped, etc.)
 * but generated protobuf TypeScript types expect camelCase (chainId, tokenId, isWrapped, etc.)
 * This schema normalizes the API response to match our generated types.
 */
const tokenInfoSchema = z
  .object({
    address: z.string(),
    decimals: z.string(),
    chain_id: z.string().optional(),
    chainId: z.string().optional(),
    token_id: z.string().optional(),
    tokenId: z.string().optional(),
    is_wrapped: z.boolean().optional(),
    isWrapped: z.boolean().optional(),
    min_withdrawal_amount: z.string().optional(),
    minWithdrawalAmount: z.string().optional(),
    commission_rate: z.string().optional(),
    commissionRate: z.string().optional(),
  })
  .transform(data => ({
    address: data.address,
    decimals: data.decimals,
    chainId: data.chainId || data.chain_id || '',
    tokenId: data.tokenId || data.token_id || '',
    isWrapped: data.isWrapped ?? data.is_wrapped ?? false,
    minWithdrawalAmount:
      data.minWithdrawalAmount || data.min_withdrawal_amount || '0',
    commissionRate: data.commissionRate || data.commission_rate || '0',
  }))

const tokenSchema = z
  .object({
    id: z.string(),
    metadata: z.any(),
    info: z.array(tokenInfoSchema).optional(),
  })
  .transform(data => ({
    id: data.id,
    metadata: data.metadata,
    info: data.info || [],
  }))

export async function fetchBridgeChains(): Promise<Chain[]> {
  const { data } = await bridgelessRestApiClient.get<{
    chains: Chain[]
  }>('/cosmos/bridge/chains')
  return data.chains
}

export async function fetchBridgeChainById(chainId: string): Promise<Chain> {
  const { data } = await bridgelessRestApiClient.get<{
    chain: Chain
  }>(`/cosmos/bridge/chains/${chainId}`)
  return data.chain
}

export async function fetchBridgeTokens(): Promise<Token[]> {
  const { data } = await bridgelessRestApiClient.get('/cosmos/bridge/tokens')
  return z.object({ tokens: z.array(tokenSchema) }).parse(data).tokens
}

export async function fetchBridgeTokenById(tokenId: string): Promise<Token> {
  const { data } = await bridgelessRestApiClient.get(
    `/cosmos/bridge/tokens/${tokenId}`,
  )
  return z.object({ token: tokenSchema }).parse(data).token
}

const referralSchema = z
  .object({
    id: z.number(),
    withdrawal_address: z.string().optional(),
    withdrawalAddress: z.string().optional(),
    commission_rate: z.string().optional(),
    commissionRate: z.string().optional(),
  })
  .transform(data => ({
    id: Number(data.id),
    withdrawalAddress: data.withdrawalAddress || data.withdrawal_address || '',
    commissionRate: data.commissionRate || data.commission_rate || '0',
  }))

export async function fetchBridgeReferrals(): Promise<Referral[]> {
  const { data } = await bridgelessRestApiClient.get('/cosmos/bridge/referrals')
  return z.object({ referrals: z.array(referralSchema) }).parse(data).referrals
}

export async function fetchBridgeReferralById(
  referralId: string,
): Promise<Referral> {
  const { data } = await bridgelessRestApiClient.get(
    `/cosmos/bridge/referrals/${referralId}`,
  )
  return z.object({ referral: referralSchema }).parse(data).referral
}
